import { useEffect, useMemo, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import AccountingToolPage from '@/components/AccountingToolPage'
import { usePageAccess } from '@/hooks/usePageAccess'
import { sendGiftCardEmail } from '@/services/giftCardEmail'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { formatGiftCardAmountInput, parseGiftCardAmount } from './giftCardAmountFormat'
import {
  clearGiftCardSendFormDraft,
  loadGiftCardSendFormDraft,
  saveGiftCardSendFormDraft,
} from './giftCardSendFormCache'

const MESSAGE_MAX_LENGTH = 500

const EMPTY_FORM = {
  amount: '',
  recipientEmail: '',
  recipientName: '',
  cardNumber: '',
  senderName: '',
  message: '',
}

function RequiredMark() {
  return (
    <span className="ml-0.5 text-red-600" aria-hidden="true">
      *
    </span>
  )
}

export default function GiftCardSend() {
  const { canEdit } = usePageAccess('gift-cards')
  const [boot] = useState(() => loadGiftCardSendFormDraft() ?? EMPTY_FORM)
  const [amount, setAmount] = useState(boot.amount)
  const [recipientEmail, setRecipientEmail] = useState(boot.recipientEmail)
  const [recipientName, setRecipientName] = useState(boot.recipientName)
  const [cardNumber, setCardNumber] = useState(boot.cardNumber)
  const [senderName, setSenderName] = useState(boot.senderName)
  const [message, setMessage] = useState(boot.message)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    saveGiftCardSendFormDraft({
      amount,
      recipientEmail,
      recipientName,
      cardNumber,
      senderName,
      message,
    })
  }, [amount, recipientEmail, recipientName, cardNumber, senderName, message])

  const amountValue = useMemo(() => parseGiftCardAmount(amount), [amount])

  const formValid = useMemo(() => {
    return amountValue > 0 && recipientEmail.trim().length > 0 && cardNumber.trim().length > 0
  }, [amountValue, recipientEmail, cardNumber])

  const handleAmountBlur = () => {
    if (!amount.trim()) return
    setAmount(formatGiftCardAmountInput(amount))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canEdit || !formValid) {
      return
    }

    setSending(true)
    try {
      await sendGiftCardEmail({
        amount: amountValue,
        recipientEmail,
        recipientName: recipientName.trim() || undefined,
        cardNumber,
        senderName: senderName.trim() || undefined,
        message: message.trim() || undefined,
      })
      toast.success('Gift card email sent.')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to send gift card email'))
    } finally {
      setSending(false)
    }
  }

  const fieldsDisabled = !canEdit || sending

  const handleReset = () => {
    clearGiftCardSendFormDraft()
    setAmount(EMPTY_FORM.amount)
    setRecipientEmail(EMPTY_FORM.recipientEmail)
    setRecipientName(EMPTY_FORM.recipientName)
    setCardNumber(EMPTY_FORM.cardNumber)
    setSenderName(EMPTY_FORM.senderName)
    setMessage(EMPTY_FORM.message)
  }

  return (
    <AccountingToolPage
      title="Gift Cards"
      description="Send a digital gift card email to a customer"
    >
      <div className="mx-auto w-full max-w-2xl">
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-5"
        >
          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="gift-card-recipient"
            >
              Recipient email
              <RequiredMark />
            </label>
            <input
              id="gift-card-recipient"
              type="email"
              required
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="gift-card-recipient-name"
            >
              Recipient name
            </label>
            <input
              id="gift-card-recipient-name"
              type="text"
              maxLength={60}
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="gift-card-amount">
              Amount (USD)
              <RequiredMark />
            </label>
            <input
              id="gift-card-amount"
              type="text"
              inputMode="decimal"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={handleAmountBlur}
              placeholder="$0.00"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="gift-card-number">
              Gift card number
              <RequiredMark />
            </label>
            <input
              id="gift-card-number"
              type="text"
              required
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="gift-card-sender">
              Sender name
            </label>
            <input
              id="gift-card-sender"
              type="text"
              maxLength={60}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="gift-card-message">
              Message
            </label>
            <textarea
              id="gift-card-message"
              rows={4}
              maxLength={MESSAGE_MAX_LENGTH}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              aria-describedby="gift-card-message-count"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={fieldsDisabled}
            />
            <p
              id="gift-card-message-count"
              className="mt-1 text-left text-xs text-gray-500"
              aria-live="polite"
            >
              {message.length} / {MESSAGE_MAX_LENGTH}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={fieldsDisabled}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canEdit || !formValid || sending}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </AccountingToolPage>
  )
}
